import ApiService from "./ApiService.js";

const DEFAULT_PROFILE_PICTURE = '/img/overlay/center/profile.jpg'

/**
 * @author Sonam (scrumier) Crumiere
 * @description Public deployment guest mode: the backend '/media/' file handler that used to
 * serve uploaded avatars was removed, but the guest login endpoint still returns a
 * 'profilePicture' pointing there. Normalize any such value (or an empty one) to the image
 * embedded in this build so it never reaches an <img src> as a dead link.
 * @param picture {undefined|string} - Raw profile picture value from the server
 * @returns {string} - A safe-to-render profile picture path
 */
function normalizeProfilePicture(picture) {
    if (!picture || picture.startsWith('/media/')) {
        return DEFAULT_PROFILE_PICTURE
    }
    return picture
}

class UserProfile {
    #id = null
    #unique_id = null
    #uniqueName = null
    #displayName = null
    #email = null
    #profilePicture = null
    #loginStatus = null
    #moodStatus = null
    #otpEnabled = false
    #isLoggedIn = false
    #isAnonymous = false
    #lastProfileUpdate = null
    #macPongCustoms = {
        ball_color: "#00FFFF",
        ball_shader: 0,
        field_shader: 0,
        firework_color:"#FFFFFF",
        paddle_color: "#FFFFFF",
        paddle_shader: 0
    }
    #profileCheckInterval = null

    /**
     * @author Corentin (ccharton) Charton
     * @description Constructor for the UserProfile class
     */
    constructor() {
        this.#profileCheckInterval = setInterval(() => this.updateProfileIfNeeded(), 30500)
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Get the user ID
     * @returns {null|string} - User ID
     */
    getID() {
        return this.#id
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Get the unique user ID
     * @returns {null|string} - Unique user ID
     */
    getUniqueId() {
        return this.#unique_id
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Get the unique username
     * @returns {null|string} - Unique user name
     */
    getUniqueName() {
        return this.#uniqueName
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the unique username
     * @param newUniqueName {null|string} - New display name
     */
    setUniqueName(newUniqueName) {
        this.#uniqueName = newUniqueName
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Get the display name
     * @returns {null|string} - Display name
     */
    getDisplayName() {
        return this.#displayName
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the display name
     * @param newDisplayName {null|string} - New display name
     */
    setDisplayName(newDisplayName) {
        this.#displayName = newDisplayName
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Get the email
     * @returns {null|string} - Email
     */
    getEmail() {
        return this.#email
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the email
     * @param newEmail {null|string} - New email
     */
    setEmail(newEmail) {
        this.#email = newEmail
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Get the profile picture
     * @returns {null|string} - Profile picture URL
     */
    getProfilePicture() {
        return this.#profilePicture
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the profile picture
     * @param profilePicture {undefined|string} - New profile picture URL
     */
    setProfilePicture(profilePicture) {
        this.#profilePicture = normalizeProfilePicture(profilePicture)
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Get the login status
     * @returns {null|string} - Login status
     */
    getLoginStatus() {
        return this.#loginStatus
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Get the mood status
     * @returns {null|string} - Mood status
     */
    getMoodStatus() {
        return this.#moodStatus
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the mood status
     * @param newMood {undefined|string} - New mood status
     */
    setMoodStatus(newMood) {
        this.#moodStatus = newMood
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Get whether OTP is enabled
     * @returns {boolean} - True if OTP is enabled, false otherwise
     */
    getOtpEnabled() {
        return this.#otpEnabled
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Get whether OTP is enabled
     * @returns {boolean} - True if OTP is enabled, false otherwise
     */
    otpChangeState() {
        this.#otpEnabled = !this.#otpEnabled
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Get whether the user is logged in
     * @returns {boolean} - True if logged in, false otherwise
     */
    isLoggedIn() {
        return this.#isLoggedIn
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the logged in status
     * @param status {boolean} - Logged in status
     */
    setLoggedIn(status) {
        if (status || typeof status === 'boolean') {
            this.#isLoggedIn = status
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Get whether the user is anonymous
     * @returns {boolean} - True if anonymous, false otherwise
     */
    isAnonymous() {
        return this.#isAnonymous
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Get the MacPong customizations
     * @returns {{ball_color: string, ball_shader: number, field_shader: number, firework_color: string, paddle_color: string, paddle_shader: number}}
     */
    getMacPongCustoms() {
        return this.#macPongCustoms
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the MacPong customizations
     * @param customs {{ball_color: string, ball_shader: number, field_shader: number, firework_color: string, paddle_color: string, paddle_shader: number}}
     */
    setMacPongCustoms(customs) {
        this.#macPongCustoms.ball_color = customs.ball_color
        this.#macPongCustoms.ball_shader = customs.ball_shader
        this.#macPongCustoms.field_shader = customs.field_shader
        this.#macPongCustoms.firework_color = customs.firework_color
        this.#macPongCustoms.paddle_color = customs.paddle_color
        this.#macPongCustoms.paddle_shader = customs.paddle_shader
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description On each call, check if the profile needs to be updated (every 30 seconds)
     */
    updateProfileIfNeeded() {
        if (!this.#lastProfileUpdate || (Date.now() - this.#lastProfileUpdate) >= 30000) {
            try {
                ApiService.getProfile(true).then((profile) => {
                    if (profile) {
                        this.setProfile(profile)
                        window.dispatchEvent(new CustomEvent('profileUpdated', { detail: profile }));
                    }
                })
            } catch (error) {
                console.error('Error updating profile:', error)
            }
        }
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Set the user profile data
     * @param data {object} - User profile data from the server
     */
    setProfile(data) {
        this.#id = data.id
        this.#unique_id = data.unique_id
        this.#uniqueName = data.uniqueName
        this.#displayName = data.displayName
        this.#email = data.email
        this.#profilePicture = normalizeProfilePicture(data.profilePicture)
        this.#loginStatus = data.loginStatus
        this.#moodStatus = data.moodStatus
        this.#otpEnabled = data.otpEnabled
        this.#isAnonymous = data.is_anonymous

        if (!data.is_anonymous) {
            this.setMacPongCustoms(data)
        }

        this.#lastProfileUpdate = Date.now()
    }

    /**
     * @author Corentin (ccharton) Charton
     * @description Clear the user profile data
     */
    clearProfile() {
        this.#id = null
        this.#unique_id = null
        this.#uniqueName = null
        this.#displayName = null
        this.#email = null
        this.#profilePicture = null
        this.#loginStatus = null
        this.#moodStatus = null
        this.#otpEnabled = false
        this.#isLoggedIn = false
        this.#isAnonymous = false
        this.#lastProfileUpdate = null

        this.setMacPongCustoms({
            ball_color: "#00FFFF",
            ball_shader: 0,
            field_shader: 0,
            firework_color:"#FFFFFF",
            paddle_color: "#FFFFFF",
            paddle_shader: 0
        })
    }
}
export default new UserProfile()